"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { SimilarJob } from "../types/job";
export function useWatchedJobs(normalizer) {
  const $ = _c(15);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [raw, setRaw] = useLocalStorage("watchedJobs", t0);
  const didMigrate = useRef(false);
  let t1;
  if ($[1] !== normalizer || $[2] !== raw) {
    t1 = normalizeWatchedJobs(raw, normalizer);
    $[1] = normalizer;
    $[2] = raw;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const normalized = t1;
  let t2;
  let t3;
  if ($[4] !== normalized || $[5] !== raw || $[6] !== setRaw) {
    t2 = () => {
      if (!didMigrate.current && raw.length > 0) {
        didMigrate.current = true;
        setRaw(normalized);
      }
    };
    t3 = [raw, setRaw, normalized];
    $[4] = normalized;
    $[5] = raw;
    $[6] = setRaw;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== normalizer || $[10] !== setRaw) {
    t4 = updater => {
      if (typeof updater === "function") {
        setRaw(previous => {
          const current = normalizeWatchedJobs(previous, normalizer);
          return updater(current);
        });
      } else {
        setRaw(updater);
      }
    };
    $[9] = normalizer;
    $[10] = setRaw;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const setWatchedJobs = t4;
  let t5;
  if ($[12] !== normalized || $[13] !== setWatchedJobs) {
    t5 = [normalized, setWatchedJobs];
    $[12] = normalized;
    $[13] = setWatchedJobs;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5 as const;
}

/**
 * Takes the raw parsed localStorage value (could be anything) and returns
 * a clean, deduplicated SimilarJob[].
 */
export function normalizeWatchedJobs<TSimilarJob extends SimilarJob = SimilarJob>(raw: unknown, normalizer?: (similarJob: SimilarJob) => TSimilarJob): TSimilarJob[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const result: TSimilarJob[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) {
      continue;
    }
    const entry = normalizeEntry(item as Record<string, unknown>);
    if (entry && !seen.has(entry.idClient)) {
      seen.add(entry.idClient);
      result.push(normalizer ? normalizer(entry) : entry as TSimilarJob);
    }
  }
  return result;
}

/**
 * Normalizes a single raw localStorage entry (which may be a legacy JobData
 * with `locations[]` or an already-migrated SimilarJob with flat `city`) into
 * the canonical SimilarJob shape.
 */
function normalizeEntry(raw: Record<string, unknown>): SimilarJob | null {
  const idClient = raw.idClient;
  const title = raw.title;
  if (typeof idClient !== "string" || typeof title !== "string") {
    return null;
  }
  const locations = raw.locations as {
    city?: string;
    country?: string;
  }[] | undefined;
  const city = typeof raw.city === "string" ? raw.city : locations?.[0]?.city ?? "";
  const country = typeof raw.country === "string" ? raw.country : locations?.[0]?.country ?? "";
  const jobField = Array.isArray(raw.jobField) ? raw.jobField[0] : raw.jobField;
  return {
    idClient,
    title,
    city,
    country,
    company: typeof raw.company === "string" ? raw.company : "",
    entryLevel: typeof raw.entryLevel === "string" ? raw.entryLevel : "",
    postingDate: typeof raw.postingDate === "string" ? raw.postingDate : "",
    jobField: typeof jobField === "string" ? jobField : "",
    applicationUrl: typeof raw.applicationUrl === "string" ? raw.applicationUrl : "",
    source: typeof raw.source === "string" ? raw.source : undefined
  };
}